import {
  forwardRef,
  memo,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { useTranslation } from 'react-i18next';
import { useDrag } from 'react-dnd';

import { Colors, Theme } from '@app/theme';
import {
  DevicesCategory,
  Participation,
  ParticipationStatus,
  QuotaTag,
  useSessionLinks,
  useSessionOrders,
} from '@app/api';
import moment from 'moment-timezone';
import {
  Chip,
  IconChip,
  NotificationPin,
  TextLoadingPlaceholder,
  TooltipText,
  PriceChips,
} from 'components';
import { getDragType } from 'lib/participationDragAndDrop';
import { ParticipationDateChip } from './ParticipationDateChip';
import { getEmptyImage } from 'react-dnd-html5-backend';
import isTouchDevice from 'is-touch-device';
import { ParticipationSourceChips } from './ParticipationSourceChips';
import {
  useIsAdmin,
  usePrefetchParticipationResponse,
  useSession,
  useSetParticipationAsViewed,
} from 'queries';
import { toPercent } from 'lib/numbers';
import {
  useParticipationDisplayName,
  useSessionUnreadMessageByParticipation,
} from '@app/core';
import { quotaLabelLimit } from './CriterionQuotaBoxLine';

const DEVICE_CATEGORY_ICONS = {
  COMPUTER: 'ri-computer-line',
  TABLET: 'ri-tablet-line',
  SMARTPHONE: 'ri-smartphone-line',
};

const useStyles = createUseStyles((theme: Theme) => ({
  card: {
    position: 'relative',
    backgroundColor: theme.colors.white,
    padding: theme.marginBase * 2,
    border: {
      width: 1,
      style: 'solid',
      color: theme.colors.grayBorder,
    },
    borderRadius: theme.borderRadius.std,
    cursor: 'pointer',
    transition: {
      property: 'box-shadow',
      duration: 150,
    },
    '&:hover': {
      boxShadow: theme.boxShadow.base,
    },
  },
  ghost: {
    cursor: 'grab',
    backgroundColor: theme.colors.grayBackground,
    '& > *': {
      opacity: 0,
    },
  },
  noBoxShadow: {
    transition: 'none',
    '&:hover': {
      boxShadow: 'none !important',
    },
  },
  participantNameContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.marginBase + 'px',
  },
  notificationSticker: {
    height: '8px',
    width: '8px',
    borderRadius: '50%',
    backgroundColor: theme.colors.red,
  },
  participantName: {
    ...theme.fonts.label,
    colors: theme.colors.inkPrimary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  billIconContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: theme.colors.white,
    padding: theme.marginBase,
  },
  billIcon: {
    fontSize: theme.iconSize.regular,
    color: theme.colors.inkTertiary,
    fontWeight: 400,
  },
  invitationDate: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    marginTop: theme.marginBase,
  },
  deviceAndDateContainer: {
    display: 'flex',
    gap: theme.marginBase / 8,
  },
  chipsLine: {
    display: 'flex',
    flexWrap: 'wrap',
    marginRight: -theme.marginBase,
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  chip: {
    marginTop: theme.marginBase,
    marginRight: theme.marginBase,
  },
  priceChips: {
    marginTop: theme.marginBase,
  },
  formatIcon: {
    marginRight: theme.marginBase,
  },
  dragging: {
    opacity: 1,
    cursor: 'grab',
  },
  chipIcon: {
    marginRight: theme.marginBase,
  },
  quotaTagText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: quotaLabelLimit + 'ch',
  },
}));

const useDraggableStyles = createUseStyles({
  hidden: {
    display: 'none',
  },
});

interface Props {
  participation?: Participation;
  className?: string;
  onClick?: (participation: Participation) => any;
  ghost?: boolean;
  dropOnColumn?: boolean;
  draggable?: boolean;
  quotaTags?: QuotaTag[];
}

export const DraggableParticipantMonitoringCard = (
  props: Props & { participation: Participation },
) => {
  const classes: any = useDraggableStyles();
  const dragType = getDragType(props.participation);
  const [{ isDragging }, drag, preview] = useDrag({
    item: {
      type: dragType,
      id: props.participation.id,
      participation: props.participation,
    },
    canDrag: dragType !== '',
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });
  useEffect(() => {
    if (!isTouchDevice()) {
      preview(getEmptyImage());
    }
  }, [preview]);

  return (
    <ParticipantMonitoringCard
      {...props}
      ref={drag}
      className={classnames(props.className, {
        [classes.hidden]: isDragging && !props.dropOnColumn,
      })}
      ghost={isDragging && props.dropOnColumn}
      quotaTags={props.quotaTags}
    />
  );
};

const MonitoringCardContent = memo(function MonitoringCardContentComp({
  participation,
  quotaTags,
}: {
  participation?: Participation;
  quotaTags?: QuotaTag[];
}) {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const sessionId = participation && participation.sessionId;
  const { data: sessionLinks } = useSessionLinks(sessionId);

  const { data: session } = useSession(sessionId);
  const isAdmin = useIsAdmin();

  const { data: orders } = useSessionOrders(
    participation?.sessionId,
    undefined,
    {
      enabled: !!participation?.sessionId && isAdmin,
    },
  );

  const unreadMessageCount = useSessionUnreadMessageByParticipation(
    sessionId,
    participation?.id,
  );

  const tags = useMemo(() => {
    if (
      !participation ||
      !session ||
      !participation.tags ||
      !session.availableTags
    ) {
      return [];
    }
    return participation.tags.map((tid) => {
      if (!session.availableTags) {
        return undefined;
      }
      return session.availableTags.find(({ id }) => id === tid);
    });
  }, [session, participation]);

  const isJustInvited = participation
    ? participation.status === ParticipationStatus.INVITED
    : false;

  const shouldDisplayNotSeenSticker =
    participation?.status === ParticipationStatus.APPLIED &&
    participation?.lastSeenByClientAt === null;

  const sessionLink = sessionLinks?.find(
    (item) => item.id === participation?.sessionLinkId,
  );

  const isDispute =
    !!sessionLink?.recruitmentByTandemz &&
    !participation?.automaticlyValidateAt &&
    participation?.status === ParticipationStatus.PARTICIPATED;

  const selectedDevice = useMemo(
    () => participation?.selectedDevice,
    [participation],
  );
  const displayDeviceIcon = useMemo(() => {
    return (
      !!participation?.appointment &&
      !quotaTags?.some(
        (quotaTag) =>
          participation?.selectedDevice === (quotaTag.item as DevicesCategory),
      )
    );
  }, [participation, quotaTags]);

  const order = useMemo(() => {
    if (!participation || !orders) {
      return undefined;
    }
    return orders.find((o) => o.participationId === participation.id);
  }, [orders, participation]);

  const name = useParticipationDisplayName(participation);
  return (
    <>
      <div className={classes.participantNameContainer}>
        {shouldDisplayNotSeenSticker && (
          <div className={classes.notificationSticker} />
        )}
        <div className={classes.participantName}>
          {name ? (
            name
          ) : (
            <TextLoadingPlaceholder sample="Mme Alexandra Raymond" />
          )}
        </div>
      </div>
      <div className={classes.billIconContainer}>
        {!!participation && participation.billed && (
          <TooltipText
            requireDiv
            title={t('sessions.details.monitoring.billed')}
          >
            <i
              className={classnames(classes.billIcon, 'ri-file-list-3-line')}
            />
          </TooltipText>
        )}
      </div>

      {participation && (
        <div className={classes.chipsLine}>
          {unreadMessageCount > 0 && (
            <IconChip
              icon="ri-chat-3-line"
              color="inkSecondary"
              label={unreadMessageCount.toString()}
              alt="chat"
              className={classes.chip}
            >
              <NotificationPin />
            </IconChip>
          )}
          {isAdmin && (participation.score || participation.score === 0) && (
            <Chip
              className={classes.chip}
              color={
                participation.score >= 85
                  ? 'green'
                  : participation.score >= 60
                  ? 'yellow'
                  : 'red'
              }
            >
              {toPercent(participation.score / 100)}
            </Chip>
          )}
          <ParticipationSourceChips
            className={classes.chip}
            participation={participation}
          />
          {!!participation && participation.documentsCount > 0 && (
            <IconChip
              icon="ri-attachment-2"
              color="inkSecondary"
              alt="attachment"
              className={classes.chip}
            />
          )}
          {isJustInvited && (
            <>
              <div className={classes.invitationDate}>
                {moment(participation.invitedAt).fromNow()}
              </div>
              {participation.invitationReminderSentAt && (
                <Chip color="yellow" className={classes.chip}>
                  {t('sessions.details.monitoring.chips.invitationReminded', {
                    date: moment(
                      participation.invitationReminderSentAt,
                    ).fromNow(),
                  })}
                </Chip>
              )}
            </>
          )}
          {participation.status === ParticipationStatus.NO_SHOW && (
            <Chip color="red" className={classes.chip}>
              {t('sessions.details.monitoring.chips.noShow', {
                context: session?.studyType?.toLowerCase(),
              })}
            </Chip>
          )}
          {participation.status === ParticipationStatus.CANCELED && (
            <Chip color="red" className={classes.chip}>
              {t('sessions.details.monitoring.chips.cancelled')}
            </Chip>
          )}
          {participation.subscriptionsCount > 1 && (
            <Chip color="red" className={classes.chip}>
              {t('sessions.details.monitoring.chips.multipleSubscriptions')}
            </Chip>
          )}
          <div className={classes.deviceAndDateContainer}>
            {!!selectedDevice && displayDeviceIcon && (
              <IconChip
                icon={DEVICE_CATEGORY_ICONS[selectedDevice]}
                className={classes.chip}
              />
            )}
            <ParticipationDateChip
              participation={participation}
              className={classes.chip}
            />
          </div>
          {(participation.status === ParticipationStatus.PAYMENT_REQUESTED ||
            participation.status ===
              ParticipationStatus.PROCESSING_PAYMENT) && (
            <Chip color="yellow" className={classes.chip}>
              <i className={classnames('ri-loader-4-line', classes.chipIcon)} />
              {t('sessions.details.monitoring.chips.processing_payment')}
            </Chip>
          )}
          {participation.isBackup && (
            <Chip color="red" className={classes.chip}>
              {t('sessions.details.monitoring.chips.backup')}
            </Chip>
          )}
          {participation.status === ParticipationStatus.PARTICIPATED &&
            participation.automaticlyValidateAt &&
            moment(participation.automaticlyValidateAt).diff(moment(), 'day') <
              2 && (
              <Chip color="red" className={classes.chip}>
                {t('sessions.details.monitoring.chips.automaticlyValidateAt', {
                  date: participation.automaticlyValidateAt,
                })}
              </Chip>
            )}
          {isDispute && (
            <Chip color="red" className={classes.chip}>
              {t('sessions.details.monitoring.chips.dispute')}
            </Chip>
          )}
        </div>
      )}
      {order && (
        <div className={classes.priceChips}>
          <PriceChips
            products={order.productLines || []}
            duration={order.duration || participation?.duration || 60}
          />
        </div>
      )}
      <div className={classes.chipsLine}>
        {tags.map(
          (tag) =>
            tag && (
              <Chip color={tag.color} className={classes.chip} key={tag.id}>
                {tag.name}
              </Chip>
            ),
        )}
      </div>
      <div className={classes.chipsLine}>
        {quotaTags?.map((tag) => (
          <Chip
            color={tag.color as Colors}
            className={classes.chip}
            key={tag.item + tag.name}
          >
            <span className={classes.quotaTagText}>{tag.name}</span>
          </Chip>
        ))}
      </div>
    </>
  );
});

export const ParticipantMonitoringCard = forwardRef(
  function ParticipantMonitoringCardComp(
    {
      participation,
      className,
      onClick: onClickProps,
      ghost,
      draggable,
      quotaTags,
    }: Props,
    ref: any,
  ) {
    const theme: Theme = useTheme();
    const classes = useStyles({ theme });

    const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const prefetchResponses = usePrefetchParticipationResponse();

    const { mutateAsync: setParticipationAsViewed } =
      useSetParticipationAsViewed(participation?.id);

    const onClick = useCallback(
      (e: SyntheticEvent<any>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!participation) {
          return;
        }
        if (onClickProps) {
          onClickProps(participation);
        }
        if (participation.status !== ParticipationStatus.INVITED) {
          setParticipationAsViewed();
        }
      },
      [onClickProps, participation, setParticipationAsViewed],
    );

    const onHover = useCallback(() => {
      if (participation) {
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = undefined;
          prefetchResponses(participation.id, participation.mainApplicationId);
        }, 200);
      }
    }, [participation, timeoutRef, prefetchResponses]);

    const onLeave = useCallback(() => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }, [timeoutRef]);

    return (
      <div
        className={classnames(
          classes.card,
          {
            [classes.ghost]: ghost,
            [classes.noBoxShadow]: !draggable,
          },
          className,
        )}
        onClick={onClickProps ? onClick : undefined}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        ref={ref}
      >
        <MonitoringCardContent
          participation={participation}
          quotaTags={quotaTags}
        />
      </div>
    );
  },
);
